/**
 * @typedef {Object} TranslationNamespace
 * @property {number} id
 * @property {string} name
 * @property {string|null} description
 * @property {number} created_at
 * @property {number} updated_at
 * @property {number} key_count
 */

/**
 * @typedef {Object} TranslationKeyListItem
 * @property {number} id
 * @property {string} key
 * @property {number} namespace_id
 * @property {string|null} namespace
 * @property {string|null} default_value
 * @property {string|null} description
 * @property {number} created_at
 * @property {number} updated_at
 * @property {number} translation_count
 */

/**
 * @typedef {Object} TranslationLocaleValue
 * @property {string} locale
 * @property {string} translated_text
 * @property {boolean} found
 * @property {number|null} value_id
 * @property {number|null} updated_at
 */

/**
 * @typedef {Object} TranslationKeyDetail
 * @property {number} id
 * @property {string} key
 * @property {number} namespace_id
 * @property {string|null} namespace
 * @property {string|null} default_value
 * @property {string|null} description
 * @property {number} created_at
 * @property {number} updated_at
 * @property {TranslationLocaleValue[]} translations
 */

/**
 * @typedef {Object} TranslationUpsertItem
 * @property {string} locale
 * @property {string} translated_text
 */

/**
 * @typedef {Object} TranslationCreatePayload
 * @property {string} key
 * @property {string} namespace
 * @property {string|null=} description
 * @property {TranslationUpsertItem[]} translations
 */

/**
 * @typedef {Object} TranslationBatchUpsertPayload
 * @property {TranslationCreatePayload[]} items
 */

/**
 * @typedef {Object} TranslationBatchUpsertResponse
 * @property {TranslationKeyDetail[]} items
 */

/**
 * @typedef {Object} TranslationUpdatePayload
 * @property {number} key_id
 * @property {string|null=} namespace
 * @property {string|null=} description
 * @property {TranslationUpsertItem[]} translations
 */

/**
 * @typedef {Object} TranslationCacheSnapshot
 * @property {number} locale_count
 * @property {number} translation_count
 * @property {number|null} loaded_at
 * @property {Object<string, number>=} locales
 */

export const TRANSLATION_LOCALES = [
  { code: 'en', label: 'English', direction: 'ltr' },
  { code: 'ar', label: 'Arabic', direction: 'rtl' },
  { code: 'de', label: 'German', direction: 'ltr' },
  { code: 'es', label: 'Spanish', direction: 'ltr' },
  { code: 'fr', label: 'French', direction: 'ltr' },
  { code: 'it', label: 'Italian', direction: 'ltr' },
  { code: 'ja', label: 'Japanese', direction: 'ltr' },
  { code: 'ko', label: 'Korean', direction: 'ltr' },
  { code: 'pt', label: 'Portuguese', direction: 'ltr' },
  { code: 'zh-hans', label: 'Simplified Chinese', direction: 'ltr' },
  { code: 'zh-hant', label: 'Traditional Chinese', direction: 'ltr' },
];

const localeByCode = Object.fromEntries(
  TRANSLATION_LOCALES.map((locale) => [locale.code, locale])
);

export const getTranslationLocale = (code) => (
  localeByCode[code] || { code, label: code, direction: 'ltr' }
);
